import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { Checkbox } from '../../../components/ui/Checkbox';
import Icon from '../../../components/AppIcon';

const AuthForm = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    firstName: '',
    lastName: '',
    company: '',
    rememberMe: false
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  const mockCredentials = {
    email: 'admin@ipsos.com',
    password: 'IpsosInsight2024!'
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e?.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    // Clear error when user starts typing
    if (errors?.[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.email) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/?.test(formData?.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData?.password) {
      newErrors.password = 'Password is required';
    } else if (formData?.password?.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    }

    if (!isLogin) {
      if (!formData?.firstName) {
        newErrors.firstName = 'First name is required';
      }
      if (!formData?.lastName) {
        newErrors.lastName = 'Last name is required';
      }
      if (!formData?.company) {
        newErrors.company = 'Company name is required';
      }
      if (formData?.password !== formData?.confirmPassword) {
        newErrors.confirmPassword = 'Passwords do not match';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (!validateForm()) return;

    setIsLoading(true);

    // Simulate API call
    setTimeout(() => {
      if (isLogin) {
        if (formData?.email === mockCredentials?.email && formData?.password === mockCredentials?.password) {
          localStorage.setItem('isAuthenticated', 'true');
          localStorage.setItem('user', JSON.stringify({
            email: formData?.email,
            firstName: 'John',
            lastName: 'Doe',
            company: 'Ipsos Research',
            role: 'Research Executive'
          }));
          navigate('/dashboard');
        } else {
          setErrors({ 
            general: `Invalid credentials. Use ${mockCredentials?.email} / ${mockCredentials?.password}` 
          });
        }
      } else {
        // Registration success
        localStorage.setItem('isAuthenticated', 'true');
        localStorage.setItem('user', JSON.stringify({
          email: formData?.email,
          firstName: formData?.firstName,
          lastName: formData?.lastName,
          company: formData?.company,
          role: 'Research Executive'
        }));
        navigate('/dashboard');
      }
      setIsLoading(false);
    }, 1500);
  };

  const handleSSOLogin = (provider) => {
    setIsLoading(true);
    // Simulate SSO authentication
    setTimeout(() => {
      localStorage.setItem('isAuthenticated', 'true');
      localStorage.setItem('user', JSON.stringify({
        email: 'john.doe@company.com',
        firstName: 'John',
        lastName: 'Doe',
        company: 'Enterprise Corp',
        role: 'Research Director',
        ssoProvider: provider
      }));
      navigate('/dashboard');
    }, 2000);
  };

  return (
    <div className="w-full max-w-md mx-auto">
      <div className="glass rounded-2xl p-8 shadow-lg border border-white/20">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-gradient-to-br from-primary to-secondary rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Icon name="Brain" size={32} color="white" strokeWidth={2} />
          </div>
          <h1 className="text-2xl font-bold text-foreground mb-2">
            {isLogin ? 'Welcome Back' : 'Create Account'}
          </h1>
          <p className="text-text-secondary">
            {isLogin 
              ? 'Sign in to access your research intelligence platform' 
              : 'Join the future of market research automation'
            }
          </p>
        </div>

        {/* Error Message */}
        {errors?.general && (
          <div className="mb-6 p-4 bg-destructive/10 border border-destructive/20 rounded-lg">
            <div className="flex items-center space-x-2">
              <Icon name="AlertCircle" size={16} className="text-destructive" />
              <span className="text-sm text-destructive">{errors?.general}</span>
            </div>
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          {!isLogin && (
            <div className="grid grid-cols-2 gap-4">
              <Input
                label="First Name"
                type="text"
                name="firstName"
                placeholder="John"
                value={formData?.firstName}
                onChange={handleInputChange}
                error={errors?.firstName}
                required
              />
              <Input
                label="Last Name"
                type="text"
                name="lastName"
                placeholder="Doe"
                value={formData?.lastName}
                onChange={handleInputChange}
                error={errors?.lastName}
                required
              />
            </div>
          )}

          {!isLogin && (
            <Input
              label="Company"
              type="text"
              name="company"
              placeholder="Your Company Name"
              value={formData?.company}
              onChange={handleInputChange}
              error={errors?.company}
              required
            />
          )}

          <Input
            label="Email Address"
            type="email"
            name="email"
            placeholder="john.doe@company.com"
            value={formData?.email}
            onChange={handleInputChange}
            error={errors?.email}
            required
          />

          <Input
            label="Password"
            type="password"
            name="password"
            placeholder="Enter your password"
            value={formData?.password}
            onChange={handleInputChange}
            error={errors?.password}
            required
          />

          {!isLogin && (
            <Input
              label="Confirm Password"
              type="password"
              name="confirmPassword"
              placeholder="Confirm your password"
              value={formData?.confirmPassword}
              onChange={handleInputChange}
              error={errors?.confirmPassword}
              required
            />
          )}

          {isLogin && (
            <div className="flex items-center justify-between">
              <Checkbox
                label="Remember me"
                name="rememberMe"
                checked={formData?.rememberMe}
                onChange={handleInputChange}
              />
              <button
                type="button"
                className="text-sm text-primary hover:text-primary/80 transition-colors duration-200"
              >
                Forgot password?
              </button>
            </div>
          )}

          <Button
            type="submit"
            variant="default"
            size="lg"
            fullWidth
            loading={isLoading}
            iconName={isLogin ? "LogIn" : "UserPlus"}
            iconPosition="left"
          >
            {isLogin ? 'Sign In' : 'Create Account'}
          </Button>
        </form>

        {/* Divider */}
        <div className="my-8 flex items-center">
          <div className="flex-1 border-t border-border"></div>
          <span className="px-4 text-sm text-text-secondary">Or continue with</span>
          <div className="flex-1 border-t border-border"></div>
        </div>

        {/* SSO Options */}
        <div className="space-y-3">
          <Button
            variant="outline"
            size="lg"
            fullWidth
            onClick={() => handleSSOLogin('microsoft')}
            iconName="Building"
            iconPosition="left"
          >
            Microsoft SSO
          </Button>
          
          <Button
            variant="outline"
            size="lg"
            fullWidth
            onClick={() => handleSSOLogin('google')}
            iconName="Chrome"
            iconPosition="left"
          >
            Google Workspace
          </Button>

          <Button
            variant="outline"
            size="lg"
            fullWidth
            onClick={() => handleSSOLogin('okta')}
            iconName="Shield"
            iconPosition="left"
          >
            Okta Enterprise
          </Button>
        </div>

        {/* Toggle Mode */}
        <div className="mt-8 text-center">
          <p className="text-sm text-text-secondary">
            {isLogin ? "Don't have an account?" : "Already have an account?"}
            <button
              type="button"
              onClick={() => {
                setIsLogin(!isLogin);
                setErrors({});
                setFormData({
                  email: '',
                  password: '',
                  confirmPassword: '',
                  firstName: '',
                  lastName: '',
                  company: '',
                  rememberMe: false
                });
              }}
              className="ml-2 text-primary hover:text-primary/80 font-medium transition-colors duration-200"
            >
              {isLogin ? 'Sign up' : 'Sign in'}
            </button>
          </p>
        </div>
      </div>
    </div>
  );
};

export default AuthForm;